// Achievements — vanity badges derived purely from a Membership's history stats.
// They are display-only and NEVER gate tier or matchmaking (project rules). The
// badge set is fixed; only the unlock thresholds live in CONFIG.
import { CONFIG, TOP_TIER } from './config.js';
/** The fixed badge catalogue, in display order. */
export const ACHIEVEMENTS = [
    { id: 'EXACT_MASTER', label: 'Exact Master', description: 'Nail the exact score' },
    { id: 'HOT_STREAK', label: 'Hot Streak', description: 'Avoid relegation, round after round' },
    { id: 'CLIMBER', label: 'Climber', description: 'Promote through the divisions' },
    { id: 'REACH_ELITE', label: 'Reach Elite', description: 'Stand at the top of the ladder' }
];
/** Whether a given membership has earned a specific achievement. */
export function hasAchievement(m, id) {
    if (!m)
        return false;
    const A = CONFIG.ACHIEVEMENTS;
    switch (id) {
        case 'EXACT_MASTER':
            return (m.exactScoreHits ?? 0) >= A.EXACT_MASTER_HITS;
        case 'HOT_STREAK':
            return (m.noRelegationStreak ?? 0) >= A.HOT_STREAK_ROUNDS;
        case 'CLIMBER':
            return (m.promotions ?? 0) >= A.CLIMBER_PROMOTIONS;
        case 'REACH_ELITE':
            return (m.highestTierReached ?? 0) >= TOP_TIER;
        default:
            return false;
    }
}
/**
 * The full catalogue annotated with whether each is earned for this membership.
 * Aggregating across several memberships (e.g. a whole career) can be done by
 * passing a synthetic membership with the max of each stat.
 */
export function deriveAchievements(m) {
    return ACHIEVEMENTS.map((a) => ({ ...a, earned: hasAchievement(m, a.id) }));
}
/**
 * Mandatory-market hit rate as a 0–100 percentage, or null when nothing has been
 * graded yet. Numerator and denominator are both server-maintained on the
 * membership (`totalCorrectMarkets` / `totalMandatoryMarkets`), so this is correct
 * regardless of how many picks a round required or how many were voided. Pass a
 * single membership for a league line, or a careerStats() result for a career.
 */
export function hitRatePercent(m) {
    if (!m)
        return null;
    const attempted = m.totalMandatoryMarkets ?? 0;
    if (attempted <= 0)
        return null;
    return Math.round(((m.totalCorrectMarkets ?? 0) / attempted) * 100);
}
/** Merge several league memberships into one "career best" stat line for badges. */
export function careerStats(memberships) {
    if (memberships.length === 0)
        return null;
    const acc = { ...memberships[0] };
    let exactScoreHits = 0;
    let promotions = 0;
    let noRelegationStreak = 0;
    let highestTierReached = 0;
    let seasonPoints = 0;
    let roundsPlayed = 0;
    let roundsWon = 0;
    let totalCorrectMarkets = 0;
    let totalMandatoryMarkets = 0;
    for (const m of memberships) {
        exactScoreHits += m.exactScoreHits ?? 0;
        promotions += m.promotions ?? 0;
        noRelegationStreak = Math.max(noRelegationStreak, m.noRelegationStreak ?? 0);
        highestTierReached = Math.max(highestTierReached, m.highestTierReached ?? 0);
        seasonPoints += m.seasonPoints ?? 0;
        roundsPlayed += m.roundsPlayed ?? 0;
        roundsWon += m.roundsWon ?? 0;
        totalCorrectMarkets += m.totalCorrectMarkets ?? 0;
        totalMandatoryMarkets += m.totalMandatoryMarkets ?? 0;
    }
    return {
        ...acc,
        exactScoreHits,
        promotions,
        noRelegationStreak,
        highestTierReached,
        seasonPoints,
        roundsPlayed,
        roundsWon,
        totalCorrectMarkets,
        totalMandatoryMarkets
    };
}
//# sourceMappingURL=achievements.js.map